import { useCallback } from 'react';
import './ReplyQuote.css';
import type { MessengerReplySnapshot } from '@/api/client';
import { FaMessengerIcon, ImageIcon } from '@/components/ui/Icons';
import { AttachmentFileBadge } from '@/components/ui/AttachmentCard/AttachmentFileBadge';
import { isMessengerImageMime } from '@/components/Messenger/shared/attachments/messengerImageMime';
import { getMessengerContextAttachmentPreview } from './messengerContextAttachmentPreview';
import {
  highlightPlainText,
  SEARCH_HIGHLIGHT_CLASS_MESSENGER,
} from '@/utils/highlightSearchTerms';

interface ReplyQuoteProps {
  replyTo: MessengerReplySnapshot;
  isOwn: boolean;
  searchHighlightWords?: readonly string[];
  onJumpToMessage?: (messageId: string) => void;
  variant?: 'default' | 'card' | 'attachmentHeader';
}

export function ReplyQuote({
  replyTo,
  isOwn,
  searchHighlightWords,
  onJumpToMessage,
  variant = 'default',
}: ReplyQuoteProps) {
  const isImage = Boolean(replyTo.file_url && isMessengerImageMime(replyTo.mime_type));
  const hasFile = Boolean(replyTo.file_url && !isImage);
  const contextPreview = getMessengerContextAttachmentPreview(replyTo.context_attachments);
  const bodyLine = (replyTo.body?.trim() ?? '').replace(/\r?\n/g, ' ');
  const preview = bodyLine
    ? bodyLine
    : isImage
      ? 'Изображение'
      : hasFile
        ? replyTo.file_name?.trim() || 'Файл'
        : contextPreview?.title || replyTo.file_name || '';

  const handleClick = useCallback(() => {
    if (onJumpToMessage) {
      onJumpToMessage(replyTo.id);
      return;
    }
    const el = document.querySelector(`[data-dm-msg="${replyTo.id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('dmBubble__highlighted');
    setTimeout(() => el.classList.remove('dmBubble__highlighted'), 1500);
  }, [onJumpToMessage, replyTo.id]);

  const forwardOrigin = replyTo.forwarded_from_sender_name?.trim();
  const senderLine = forwardOrigin ? (
    <span className="replyQuote__senderForward">
      <FaMessengerIcon icon="share" size={13} className="replyQuote__forwardIcon" />
      {forwardOrigin}
    </span>
  ) : (
    replyTo.sender_name
  );
  const replyAriaLabel = forwardOrigin
    ? `Перейти к пересланному сообщению от ${forwardOrigin}`
    : `Перейти к сообщению от ${replyTo.sender_name}`;

  return (
    <button
      type="button"
      className={[
        'replyQuote',
        isOwn ? 'replyQuote_own' : 'replyQuote_peer',
        variant === 'card' ? 'replyQuote_card' : '',
        variant === 'attachmentHeader' ? 'replyQuote_attachmentHeader' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      aria-label={replyAriaLabel}
    >
      {isImage && replyTo.file_url ? (
        <img
          src={replyTo.file_url}
          alt=""
          className="replyQuote__thumb"
          draggable={false}
          aria-hidden
        />
      ) : null}
      <div className="replyQuote__bar" />
      <div className="replyQuote__content">
        <span className="replyQuote__sender">{senderLine}</span>
        <span className="replyQuote__preview">
          {isImage ? (
            <ImageIcon size={13} className="replyQuote__fileIcon" />
          ) : hasFile || contextPreview ? (
            <AttachmentFileBadge
              fileName={replyTo.file_name || contextPreview?.title || 'file'}
              kind={contextPreview?.kind ?? 'file'}
              size="sm"
              className="replyQuote__fileIcon"
            />
          ) : null}
          <span className="replyQuote__previewText">
            {preview
              ? searchHighlightWords?.length
                ? highlightPlainText(preview, [...searchHighlightWords], SEARCH_HIGHLIGHT_CLASS_MESSENGER)
                : preview
              : (
                <em>Сообщение удалено</em>
              )}
          </span>
        </span>
      </div>
    </button>
  );
}
